import dayjs from "dayjs";
import Decimal from "decimal.js";
import $ from "jquery";
import JSZip from "jszip";
import type { ImageFormat } from "./Downloader";
import { Button } from "./Form";
import { outputFilePrefix } from "./OutputName";
import type { RenderCoordinator } from "./RenderCoordinator";
import type { VideoHost } from "./VideoRecorder";

// Downloader が構造的に満たすフレーム供給インターフェイス (NOWC 等の別実装も可)
export interface ImageZipFrameSource {
    getSelectedSize(): [number, number];
    getSelectedFormat(): ImageFormat;
    toBlob(width: number, height: number, format: ImageFormat): Promise<Blob>;
}

export interface ImageZipRequest {
    ftStart?: number;
    ftEnd?: number;
    ftStep?: number;
    width?: number;
    height?: number;
    format?: ImageFormat;
    filenamePrefix?: string; // zip内ファイル名の先頭 (種別など)。未指定は従来どおり
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
}

export interface ImageZipMeta {
    frames: number;
    width: number;
    height: number;
    format: ImageFormat;
    ftStart: string;
    ftEnd: string;
    ftStep: string;
    elapsedMs: number;
    filenames: string[];
}

export interface ImageZipResult {
    blob: Blob;
    meta: ImageZipMeta;
}

const MAX_FRAMES = 500;

export class ImageZipExporter {
    private readonly link = document.querySelector("#link_png")!;
    private abortController: AbortController | null = null;
    private objectUrl: string | null = null;

    // bindUi=false は create()/abort() を直接呼ぶ利用 (NOWC250-2.0 等)。
    // GPV標準UIでは専用ボタンを持たず、「画像生成」の時間幅>0でApp側から
    // downloadZipFromUi() が呼ばれる
    constructor(
        private readonly host: VideoHost,
        private readonly downloader: ImageZipFrameSource,
        private readonly coordinator: RenderCoordinator,
        bindUi = true,
    ) {
        if (bindUi) {
            new Button("#toPNGs_stop").onClick(() => this.abort());
        }
        window.addEventListener("beforeunload", () => this.revokeObjectUrl());
    }

    get running() {
        return this.abortController !== null;
    }

    abort() {
        this.abortController?.abort(
            new DOMException("連番画像作成を中止しました。", "AbortError"),
        );
    }

    async create(request: ImageZipRequest): Promise<ImageZipResult> {
        if (this.running) {
            throw new Error("連番画像作成は同時に1件までです。");
        }
        this.abortController = new AbortController();
        try {
            await this.host.waitIdle();
        } catch (error) {
            this.abortController = null;
            throw error;
        }
        const task = this.run(request, this.abortController.signal).finally(
            () => {
                this.abortController = null;
            },
        );
        this.coordinator.track("zip", task).catch(() => {
            this.coordinator.clearError("zip");
        });
        return task;
    }

    private uiRequest(): ImageZipRequest {
        const hoursInput = Number($("#input_topngs_hour").val());
        const hours =
            Number.isFinite(hoursInput) && hoursInput > 0 ? hoursInput : 0;
        const range = this.host.getRange();
        const start = this.host.getForecastTime();
        return {
            ftStart: start.toNumber(),
            ftEnd: Decimal.min(start.add(hours), range.maximum).toNumber(),
            ftStep: range.interval.toNumber(),
            format: this.downloader.getSelectedFormat(),
            filenamePrefix: `${outputFilePrefix()}_`,
        };
    }

    private setUiBusy(busy: boolean) {
        for (const selector of ["#download_image", "#to_avi"]) {
            const unavailable =
                selector === "#to_avi" &&
                $(selector).attr("data-video-unavailable") === "true";
            $(selector).prop("disabled", busy || unavailable);
        }
        if (busy) {
            $("#toPNGs_stop").show();
        } else {
            $("#toPNGs_stop").hide();
        }
    }

    // 「画像生成」の時間幅>0 (複数枚)。ZIPは従来どおりDL処理へ渡す
    async downloadZipFromUi() {
        this.setUiBusy(true);
        this.link.textContent = "連番画像作成中...";
        try {
            const { blob, meta } = await this.create({
                ...this.uiRequest(),
                onProgress: (done, total) => {
                    this.link.textContent = `連番画像作成中: ${done} / ${total}`;
                },
            });
            this.revokeObjectUrl();
            this.objectUrl = URL.createObjectURL(blob);
            const filename = `${outputFilePrefix()}_${dayjs().format("YYYYMMDD_HHmmss")}.zip`;
            this.link.textContent = "";
            const anchor = document.createElement("a");
            anchor.href = this.objectUrl;
            anchor.download = filename;
            anchor.textContent = `連番画像完成 (${meta.frames}枚): ${filename}`;
            this.link.appendChild(anchor);
            anchor.click();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : String(error);
            this.link.textContent = `連番画像作成失敗: ${message}`;
            console.error(error);
        } finally {
            this.setUiBusy(false);
        }
    }

    private async run(
        request: ImageZipRequest,
        signal: AbortSignal,
    ): Promise<ImageZipResult> {
        const started = performance.now();
        const range = this.host.getRange();
        const step = new Decimal(request.ftStep ?? range.interval);
        const start = new Decimal(
            request.ftStart ?? this.host.getForecastTime(),
        );
        const end = new Decimal(request.ftEnd ?? range.maximum);
        if (!step.isFinite() || step.lte(0)) {
            throw new Error("FT刻みが不正です。");
        }
        if (
            !start.isFinite() ||
            !end.isFinite() ||
            start.lt(range.minimum) ||
            end.gt(range.maximum) ||
            start.gt(end)
        ) {
            throw new Error(
                `FT範囲が不正です (${range.minimum}〜${range.maximum} の範囲で指定してください)。`,
            );
        }
        const total = end.sub(start).div(step).floor().toNumber() + 1;
        if (total > MAX_FRAMES) {
            throw new Error(
                `画像数が多すぎます (${total} > ${MAX_FRAMES})。FT範囲か刻みを見直してください。`,
            );
        }
        const frameFts = Array.from({ length: total }, (_, index) =>
            start.add(step.mul(index)),
        );
        await this.host.prepareFrames?.(frameFts);
        const [selectedWidth, selectedHeight] =
            this.downloader.getSelectedSize();
        const width = request.width ?? selectedWidth;
        const height = request.height ?? selectedHeight;
        const format = request.format ?? this.downloader.getSelectedFormat();
        if (!(["png", "jpeg", "webp"] as string[]).includes(format)) {
            throw new Error("画像形式が不正です。");
        }

        const zip = new JSZip();
        const filenames: string[] = [];
        const originalFt = this.host.getForecastTime();
        try {
            for (let index = 0; index < total; index++) {
                signal.throwIfAborted();
                request.signal?.throwIfAborted();
                const ft = frameFts[index];
                await this.host.renderFrame(ft);
                signal.throwIfAborted();
                request.signal?.throwIfAborted();
                const blob = await this.downloader.toBlob(
                    width,
                    height,
                    format,
                );
                const filename =
                    (request.filenamePrefix ?? "") +
                    this.frameFilename(index, ft, format);
                filenames.push(filename);
                zip.file(filename, blob);
                request.onProgress?.(index + 1, total);
            }
            signal.throwIfAborted();
            request.signal?.throwIfAborted();
            const blob = await zip.generateAsync({
                type: "blob",
                compression: "STORE",
                streamFiles: true,
            });
            return {
                blob,
                meta: {
                    frames: total,
                    width,
                    height,
                    format,
                    ftStart: start.toString(),
                    ftEnd: start.add(step.mul(total - 1)).toString(),
                    ftStep: step.toString(),
                    elapsedMs: Math.round(performance.now() - started),
                    filenames,
                },
            };
        } finally {
            await this.host
                .renderFrame(originalFt)
                .catch((error) => console.error(error));
        }
    }

    private frameFilename(index: number, ft: Decimal, format: ImageFormat) {
        const extension = format === "jpeg" ? "jpg" : format;
        const sign = ft.isNegative() ? "PT" : "FT";
        const value = ft.abs().toFixed(1).padStart(5, "0");
        return `${String(index + 1).padStart(4, "0")}_${sign}${value}.${extension}`;
    }

    private revokeObjectUrl() {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}
